import { useState, type ComponentProps } from 'react';
import {
  Button,
  Heading,
  Paragraph,
  PlainPopoverTrigger,
  Popover,
  PopoverContent,
  PopoverTriggerButton,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { GatedFeaturesCallToAction } from './gated-features-call-to-action';

type GatedFeaturesTriggerProps =
  | ({ triggerVariant?: 'button' } & ComponentProps<typeof PopoverTriggerButton>)
  /** An unstyled trigger, for wrapping content that isn't button-shaped (e.g. a card). */
  | ({ triggerVariant: 'plain' } & ComponentProps<typeof PlainPopoverTrigger>);

type GatedFeaturesPopoverProps = GatedFeaturesTriggerProps & {
  featureName?: string;
  title?: string;
  text?: string;
};

function GatedFeaturesTrigger(props: GatedFeaturesTriggerProps) {
  if (props.triggerVariant === 'plain') {
    const { triggerVariant: _variant, ...plainProps } = props;
    return <PlainPopoverTrigger {...plainProps} />;
  }
  const { triggerVariant: _variant, ...buttonProps } = props;
  return <PopoverTriggerButton {...buttonProps} />;
}

/**
 * An upgrade-prompt popover whose trigger is a button (the remaining props are
 * forwarded to it). Clicking the popover's CTA opens the gated-features
 * confirmation modal. Port of the Ember `gated-features/popover` component.
 */
export function GatedFeaturesPopover({ featureName, title, text, ...triggerProps }: GatedFeaturesPopoverProps) {
  const { account } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Essentials-trial accounts are pitched a Premium *trial* rather than a sales
  // conversation (mirrors the Ember popover's `isEssentialsTrial` check).
  const isEssentialsTrial = account.suspend_trial_at != null;

  return (
    <GatedFeaturesCallToAction featureName={featureName}>
      {({ trigger }) => (
        <Popover isOpen={isOpen} onOpenChange={(open) => setIsOpen(open)} shouldTriggerOnClick placement="bottom">
          <GatedFeaturesTrigger {...triggerProps} />
          <PopoverContent>
            <Heading level="3" mb="050" data-test-gated-features-popover-title>
              {title}
            </Heading>
            <Paragraph mb="200" data-test-gated-features-popover-text>
              {text}
            </Paragraph>
            <Button
              data-test-gated-features-popover-btn
              variant="primary"
              onClick={() => {
                setIsOpen(false);
                trigger();
              }}
            >
              {isEssentialsTrial ? 'Switch to a Premium trial' : "Let's talk"}
            </Button>
          </PopoverContent>
        </Popover>
      )}
    </GatedFeaturesCallToAction>
  );
}
